// originally from Minimal UI Kit template file `src/serviceWorkerRegistration`

// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

import loggerService, { LoggerLevel } from 'common/infrastructure/log';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

// Only reload when we detect an actual update - when a new service worker is installed
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  let isReloading = false;

  // Listen for service worker controller changes
  // This fires when a new service worker takes control
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Only reload if we explicitly set the flag that an update was detected
    const updateDetected = sessionStorage.getItem('sw-update-detected') === 'true';

    if (!updateDetected || isReloading) {
      return;
    }

    // Clear the flag and reload
    sessionStorage.removeItem('sw-update-detected');
    isReloading = true;

    loggerService.log(
      LoggerLevel.INFO,
      'New service worker activated. Reloading to get latest version...'
    );

    window.location.reload();
  });
}

export function register(config?: Config) {
  // TODO: check env or env var to setup
  // process.env.NODE_ENV === 'production' &&
  if ('serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(window.location.href);

    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${window.location.origin}${import.meta.env.BASE_URL}service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl, { type: 'module' })
    .then((registration) => {
      // Track the current service worker script URL to detect actual changes
      let currentSwUrl = registration.active?.scriptURL || registration.installing?.scriptURL;

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            // Check if this is actually a new service worker (different script URL)
            const newSwUrl = installingWorker.scriptURL;
            const isActualUpdate = currentSwUrl && newSwUrl && newSwUrl !== currentSwUrl;

            if (navigator.serviceWorker.controller && isActualUpdate) {
              // This is a real update - set the flag so controllerchange will reload
              sessionStorage.setItem('sw-update-detected', 'true');
              currentSwUrl = newSwUrl;

              loggerService.log(
                LoggerLevel.INFO,
                'New content is available. Service worker will activate and reload automatically.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else if (!navigator.serviceWorker.controller) {
              // First install - no reload needed
              loggerService.log(LoggerLevel.INFO, 'Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
      return;
    })
    .catch((error) => {
      loggerService.log(LoggerLevel.ERROR, 'Error during service worker registration', error);
    });
}

async function checkValidServiceWorker(swUrl: string, config?: Config) {
  try {
    // Check if the service worker can be found. If it can't reload the page.
    const response = await fetch(swUrl, {
      headers: { 'Service-Worker': 'script' },
    });

    // Ensure service worker exists, and that we really are getting a JS file.
    const contentType = response.headers.get('content-type');

    // when file not available, contentType: "text/html; charset=utf-8" & status: 200
    if (
      response.status === 404 ||
      (contentType != null && contentType.indexOf('javascript') === -1)
    ) {
      // No service worker found. Probably a different app. Reload the page.
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.unregister();
        window.location.reload();
      } catch (error) {
        loggerService.log(LoggerLevel.ERROR, 'Error unregistering service worker', error);
      }
      return;
    }

    registerValidSW(swUrl, config);
  } catch (error) {
    loggerService.log(
      LoggerLevel.INFO,
      'No internet connection found. App is running in offline mode.'
    );
  }
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => {
        loggerService.log(LoggerLevel.ERROR, 'Failed to unregister service worker', error);
      });
  }
}
